import {
  CBadge,
  CButton,
  CCol,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CRow,
} from '@coreui/react-pro'
import React, { useState } from 'react'
import PlayerSponsorService from 'src/service/PlayerSponsorService'
import ToastComponent from 'src/components/common/TaostComponent'
import ImageDropzone from 'src/components/common/ImageDropzone'

const emptyFields = { name: '', website: '', facebook: '', twitter: '', instagram: '' }

const sponsorLogoUrl = (logo) =>
  logo ? `${process.env.REACT_APP_API_URL}uploads/sponsor/${logo}` : null

const SponsorshipTab = ({ detail, playerId, onSaved, onCancel, onToggleStatus, isActive }) => {
  const [mode, setMode] = useState('existing') // 'existing' | 'new'
  const [selectedSponsorId, setSelectedSponsorId] = useState('')
  const [fields, setFields] = useState(emptyFields)
  const [logoFile, setLogoFile] = useState(null)
  // Logo of the sponsor picked from the dropdown: previewed here, and copied server-side
  // into this player's own record unless a new file is uploaded over it.
  const [templateLogo, setTemplateLogo] = useState(null)
  const [editing, setEditing] = useState(false)
  const [loader, setLoader] = useState(false)

  const sponsor = detail.sponsorDetail
  const sponsorOptions = detail.sponsorOptions || []

  const setField = (key, value) => setFields((prev) => ({ ...prev, [key]: value }))

  const applyTemplate = (id) => {
    setSelectedSponsorId(id)
    const template = sponsorOptions.find((s) => String(s.id) === String(id))
    if (template) {
      setFields({
        name: template.name || '',
        website: template.website || '',
        facebook: template.facebook || '',
        twitter: template.twitter || '',
        instagram: template.instagram || '',
      })
      setTemplateLogo(template.logo || null)
      setLogoFile(null)
    } else {
      setFields(emptyFields)
      setTemplateLogo(null)
    }
  }

  const buildFormData = () => {
    const formData = new FormData()
    formData.append('player_id', playerId)
    formData.append('name', fields.name)
    formData.append('website', fields.website)
    formData.append('facebook', fields.facebook)
    formData.append('twitter', fields.twitter)
    formData.append('instagram', fields.instagram)
    if (logoFile) formData.append('image', logoFile)
    else if (mode === 'existing' && templateLogo && selectedSponsorId) {
      formData.append('copy_logo_from', selectedSponsorId)
    }
    return formData
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    if (mode === 'existing' && !selectedSponsorId) {
      ToastComponent('Please select a sponsor', 'error')
      return
    }
    if (!logoFile && !templateLogo) {
      ToastComponent('Sponsor logo is required', 'error')
      return
    }
    if (!fields.name) {
      ToastComponent('Sponsor name is required', 'error')
      return
    }
    setLoader(true)
    try {
      const res = await PlayerSponsorService.saveSponsor(buildFormData())
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
        onSaved()
      } else {
        ToastComponent(res.message, 'error')
      }
    } catch (e2) {
      ToastComponent('Something went wrong', 'error')
    }
    setLoader(false)
  }

  const handleEditSave = async (e) => {
    e.preventDefault()
    setLoader(true)
    const formData = buildFormData()
    formData.append('sponsorId', sponsor.id)
    try {
      const res = await PlayerSponsorService.editSponsor(formData)
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
        setEditing(false)
        onSaved()
      } else {
        ToastComponent(res.message, 'error')
      }
    } catch (e2) {
      ToastComponent('Something went wrong', 'error')
    }
    setLoader(false)
  }

  const handleRemove = async () => {
    setLoader(true)
    const res = await PlayerSponsorService.deleteSponsor({ id: sponsor.id })
    if (res.status === 200) {
      ToastComponent('Sponsor removed.', 'success')
      onSaved()
    }
    setLoader(false)
  }

  const startEditing = () => {
    setFields({
      name: sponsor.name || '',
      website: sponsor.website || '',
      facebook: sponsor.facebook || '',
      twitter: sponsor.twitter || '',
      instagram: sponsor.instagram || '',
    })
    setLogoFile(null)
    setTemplateLogo(null)
    setEditing(true)
  }

  const footer = (
    <div className="d-flex justify-content-between align-items-center mt-4">
      <CButton
        color="danger"
        variant="ghost"
        type="button"
        onClick={() => onToggleStatus(isActive ? 0 : 1)}
      >
        {isActive ? 'Deactivate' : 'Activate'}
      </CButton>
      <CButton color="secondary" variant="outline" type="button" onClick={onCancel}>
        Close
      </CButton>
    </div>
  )

  // Already has a sponsor and not actively editing it — show the assigned summary.
  if (sponsor && !editing) {
    return (
      <div>
        <div className="fw-bold mb-2">Player Sponsor</div>
        <div className="text-medium-emphasis small mb-3">
          This player&apos;s sponsor record. Editing here only changes this player&apos;s entry.
        </div>
        <div className="d-flex align-items-center border rounded-3 p-3">
          {sponsorLogoUrl(sponsor.logo) ? (
            <img
              src={sponsorLogoUrl(sponsor.logo)}
              alt={sponsor.name}
              width={48}
              height={48}
              className="rounded-2 me-3"
              style={{ objectFit: 'contain' }}
            />
          ) : (
            <div
              className="rounded-2 bg-light me-3"
              style={{ width: 48, height: 48, flexShrink: 0 }}
            />
          )}
          <div className="flex-grow-1">
            <div className="fw-semibold">{sponsor.name}</div>
            {sponsor.website && (
              <a href={sponsor.website} target="_blank" rel="noreferrer" className="small">
                {sponsor.website}
              </a>
            )}
          </div>
          <CBadge color="success">Assigned</CBadge>
        </div>

        <div className="d-flex gap-3 mt-3">
          <CButton color="link" className="p-0" onClick={startEditing}>
            Edit Sponsor Details
          </CButton>
          <CButton
            color="link"
            className="p-0 text-danger"
            onClick={handleRemove}
            disabled={loader}
          >
            Remove Sponsor
          </CButton>
        </div>

        {footer}
      </div>
    )
  }

  // Editing an already-assigned sponsor's own details.
  if (sponsor && editing) {
    return (
      <form onSubmit={handleEditSave}>
        <div className="fw-bold mb-3">Edit Sponsor Details</div>
        <SponsorFields
          fields={fields}
          setField={setField}
          setLogoFile={setLogoFile}
          logoFile={logoFile}
          existingLogoUrl={sponsorLogoUrl(sponsor.logo)}
        />
        <div className="d-flex justify-content-between align-items-center mt-4">
          <CButton
            color="secondary"
            variant="outline"
            type="button"
            onClick={() => setEditing(false)}
          >
            Cancel
          </CButton>
          <CLoadingButton type="submit" color="primary" loading={loader}>
            Save Sponsorship
          </CLoadingButton>
        </div>
      </form>
    )
  }

  // No sponsor yet — choose to assign an existing one as a template, or create a brand new one.
  return (
    <form onSubmit={handleCreate}>
      <div className="fw-bold mb-2">Player Sponsor</div>
      <div className="text-medium-emphasis small mb-3">
        Assign a sponsor to this player&apos;s profile.
      </div>

      <div className="d-flex gap-2 mb-3">
        <CFormCheck
          button={{ color: 'secondary', variant: 'outline' }}
          type="radio"
          name="sponsor-mode"
          id="sponsor-mode-existing"
          autoComplete="off"
          label="Assign Existing Sponsor"
          checked={mode === 'existing'}
          onChange={() => {
            setMode('existing')
            applyTemplate(selectedSponsorId)
          }}
        />
        <CFormCheck
          button={{ color: 'secondary', variant: 'outline' }}
          type="radio"
          name="sponsor-mode"
          id="sponsor-mode-new"
          autoComplete="off"
          label="Create New Sponsor"
          checked={mode === 'new'}
          onChange={() => {
            setMode('new')
            setFields(emptyFields)
            setSelectedSponsorId('')
            setTemplateLogo(null)
          }}
        />
      </div>

      {mode === 'existing' && (
        <CCol md={12} className="mb-3">
          <CFormLabel htmlFor="sponsor-template">Sponsor</CFormLabel>
          <CFormSelect
            id="sponsor-template"
            value={selectedSponsorId}
            onChange={(e) => applyTemplate(e.target.value)}
          >
            <option value="">Select a sponsor</option>
            {sponsorOptions.map((item) => (
              <option value={item.id} key={item.id}>
                {item.name}
              </option>
            ))}
          </CFormSelect>
          <div className="text-medium-emphasis small mt-1">
            Copies this sponsor&apos;s details into a new record for this player — editing it later
            won&apos;t affect the original.
          </div>
        </CCol>
      )}

      <SponsorFields
        fields={fields}
        setField={setField}
        setLogoFile={setLogoFile}
        logoFile={logoFile}
        existingLogoUrl={mode === 'existing' ? sponsorLogoUrl(templateLogo) : null}
      />

      <div className="d-flex justify-content-between align-items-center mt-4">
        <CButton
          color="danger"
          variant="ghost"
          type="button"
          onClick={() => onToggleStatus(isActive ? 0 : 1)}
        >
          {isActive ? 'Deactivate' : 'Activate'}
        </CButton>
        <div className="d-flex gap-2">
          <CButton color="secondary" variant="outline" type="button" onClick={onCancel}>
            Cancel
          </CButton>
          <CLoadingButton type="submit" color="primary" loading={loader}>
            Save Sponsorship
          </CLoadingButton>
        </div>
      </div>
    </form>
  )
}

const SponsorFields = ({ fields, setField, setLogoFile, logoFile, existingLogoUrl }) => (
  <CRow className="g-3">
    <CCol md={6}>
      <CFormLabel htmlFor="sponsor-name">Sponsor Name</CFormLabel>
      <CFormInput
        id="sponsor-name"
        value={fields.name}
        onChange={(e) => setField('name', e.target.value)}
      />
    </CCol>
    <CCol md={6}>
      <CFormLabel htmlFor="sponsor-website">Website</CFormLabel>
      <CFormInput
        id="sponsor-website"
        placeholder="https://"
        value={fields.website}
        onChange={(e) => setField('website', e.target.value)}
      />
    </CCol>
    <CCol md={4}>
      <CFormLabel htmlFor="sponsor-facebook">Facebook URL</CFormLabel>
      <CFormInput
        id="sponsor-facebook"
        value={fields.facebook}
        onChange={(e) => setField('facebook', e.target.value)}
      />
    </CCol>
    <CCol md={4}>
      <CFormLabel htmlFor="sponsor-twitter">Twitter URL</CFormLabel>
      <CFormInput
        id="sponsor-twitter"
        value={fields.twitter}
        onChange={(e) => setField('twitter', e.target.value)}
      />
    </CCol>
    <CCol md={4}>
      <CFormLabel htmlFor="sponsor-instagram">Instagram URL</CFormLabel>
      <CFormInput
        id="sponsor-instagram"
        value={fields.instagram}
        onChange={(e) => setField('instagram', e.target.value)}
      />
    </CCol>
    <CCol md={12}>
      <CFormLabel htmlFor="sponsor-logo">Sponsor Logo</CFormLabel>
      <ImageDropzone
        minHeight={120}
        file={logoFile}
        existingUrl={existingLogoUrl}
        onChange={(selected) => setLogoFile(selected)}
      />
    </CCol>
  </CRow>
)

export default SponsorshipTab
